// LEGACY (pre scaling Phase 1) — copy of main as of 2026-10-27, served to
// workspaces WITHOUT the `social_graph_v2` feature flag (see ../SocialGraph.tsx).
// When adding a feature to the v2 page, ask whether the legacy version needs it
// too (it stays in production until v2 is confirmed), then apply it here as well
// if so. Delete the whole `legacy/` folder once the flag is retired.
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useCurrentUser, CurrentUser } from '../../../contexts/CurrentUser';
import { API } from '../../../API';
import Graph from 'graphology';
import type { LayoutMode, LayoutWorkerRequest, LayoutWorkerResult } from './layoutPipeline';
import {
  ActiveGraphKind,
  ActorGroupMember,
  ACTOR_ROLE_DESCRIPTIONS,
  AnalysisMode,
  classicEdgeSize,
  EdgeAttributes,
  edgeVolumeSize,
  EXPLORE_GRAPH_DEFAULTS,
  GraphColorMode,
  GraphHighlight,
  GraphStats,
  NodeAttributes,
  NodeFilterCategory,
  NodeFilterOption,
  NodeFilters,
  NodeConnection,
  FlagGroup,
  MemberConnections,
  NodeFlag,
  NODE_FLAG_DESCRIPTIONS,
  NODE_FLAG_LABELS,
  DEFAULT_SIZE_METRIC,
  NodeSizeMetric,
  SelectedNodeInfo,
  TOP_VOLUME_MIN_EDGES,
  ZScoreBucket,
  AUDIENCE_BUCKET_ORDER,
  EMPTY_NODE_FILTERS,
  hasActiveNodeFilters,
  HIDDEN_ACTOR_ROLE_CATEGORIES,
  matchesNodeFilters,
  ZSCORE_BUCKETS,
  ZSCORE_BUCKET_DESCRIPTIONS,
  ZSCORE_BUCKET_LABELS,
  ZSCORE_ENABLED,
} from '../graphTypes';
import { categoryHighlightColor, computeEdgeColor, computeNodeColor, CONTEXT_EDGE_MUTE, DEFAULT_COLOR_MODE, DEFAULT_INTER_CLUSTER_MODE, DIMMED_NODE_COLOR, InterClusterMode, isConnectionSurfacedFlow, LABEL_TEXT_COLOR, mixWithBackground, zscoreEdgeStyle, zscoreNodeBucket } from '../graphColors';
// canonical uk/ru → name_uk locale check used by the tag components
import { isUkLocale } from '../../../components/TagsTree';
import { buildFilterChips, buildNodeFilterSections, FilterChipId } from '../filterSections';
import { FilterChips } from '../FilterChips';
import { actorConnectionStats, buildCaseGraphFromResponse, buildGraphFromResponse, memberConnections, rankPlatformColors } from './buildGraph';
import { createDrawNodeLabel } from '../drawNodeLabel';
import { GraphHoverEffects } from './GraphHoverEffects';
import Sigma from 'sigma';
import { NodePanel } from '../NodePanel';
import { GroupPanel, HighlightGroupInfo } from '../GroupPanel';
import { GraphPanel } from '../GraphPanel';
import { GraphLegend, GraphLegendProps, hasLegendContent } from '../GraphLegend';
import { CanvasControls } from '../CanvasControls';
import { ReactComponent as ChevronIcon } from '../assets/chevron.svg';
import { GraphHeader } from '../GraphHeader';
import { GraphGuidePanel } from '../guide/GraphGuidePanel';
import { GraphGuideContext } from '../guide/GraphGuideContext';
import { DEFAULT_GUIDE_PAGE, GuidePageId, guideVariantFor } from '../guide/guideContent';
import { GraphHostActions } from '../hostActions';
import { SigmaRefSync } from '../SigmaRefSync';
import { SocialGraphAnalyticsContext, useSocialGraphTracker } from '../analytics';
import { WebGLContextLossRecovery } from '../WebGLContextLossRecovery';
import { PixelRatioSync } from '../PixelRatioSync';
import type { CameraState } from 'sigma/types';
import { exportGraphAsPng, exportGraphAsSvg } from '../graphExport';
import { SigmaContainer } from '@react-sigma/core';
import EdgeCurveProgram, { EdgeCurvedArrowProgram } from '@sigma/edge-curve';
import '@react-sigma/core/lib/style.css';
import { socialGraphApi, SocialGraphModesResponse, CaseGraphResponse, SavedGraphDetailDTO, SocialGraphReportDTO, SocialGraphReportLang } from '../../../utils/socialGraphApi';
import { AddToSourceGroupModal } from '../../../components/AddToSourceGroupModal/AddToSourceGroupModal';
import websocketConnection from '../../../websocket';
import {
  aggregateTimeline,
  aggregateCaseTimeline,
  getActiveEdgeKeys,
  getEdgeKeysForPeriod,
  getCaseActiveEdgeKeys,
  getCaseEdgeKeysForPeriod,
  getActiveNodeIds,
  MENTION_TYPE_COLORS,
  MENTION_TYPE_LABELS,
} from './timelineUtils';
import { SocialGraphTimeline } from '../SocialGraphTimeline';
import { TimelineOverviewStrip } from '../TimelineOverviewStrip';
import { NodeGlowCircleProgram } from '../nodeGlowProgram';
import { showRequestError } from '../../../utils/requestError';
import styles from '../SocialGraphPOC.module.scss';

// the modal is untyped JSX and TS marks every destructured prop as required;
// this alias narrows it to the source-ids path the graph actually uses
const AddActorsToGroupModal = AddToSourceGroupModal as unknown as React.FC<{
  isOpen: boolean;
  sources: { id: string }[];
  widgetName?: string;
  dark?: boolean;
  onChange: (open: boolean) => void;
}>;

// timeline playback speed multipliers (Figma Timeline / Speed 501-27)
const PLAY_SPEEDS = [0.5, 1, 2, 4] as const;

// stable series order for the timeline chart and its header legend
const TIMELINE_SERIES_ORDER = ['MENTION', 'REPOST', 'LINK'];

interface SocialGraphPOCProps {
  // Embedded (overlay) mode: load this graph instead of the route param, and
  // let the host close the view. The /graphs/:graphId route passes neither.
  graphId?: number;
  onExit?: () => void;
  // Artifact tab: the graph comes from the artifact registry (no social_graph
  // feature flag involved) — read-only view, no report/exit/graphs navigation
  graphSource?: (signal?: AbortSignal) => Promise<SavedGraphDetailDTO>;
}

// WebGL context-loss recovery budget (see WebGLContextLossRecovery)
const CONTEXT_LOSS_MAX_RECOVERIES = 3;
const CONTEXT_LOSS_WINDOW_MS = 30_000;

export const SocialGraphPOC: React.FC<SocialGraphPOCProps> = ({
  graphId: graphIdProp,
  onExit,
  graphSource,
}) => {
  const [currentUser, setCurrentUser] = useCurrentUser();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { graphId: graphIdParam } = useParams<{ graphId: string }>();
  const graphId = graphIdProp ?? graphIdParam;
  // embedded = rendered inside GraphViewOverlay on a host page (which sits in
  // Layout and already has the app ToastContainer)
  const isEmbedded = graphIdProp != null;
  const isReadOnlyHost = graphSource != null;
  const [mode, setMode] = useState<AnalysisMode>('exploratory');
  const [sizeMetric, setSizeMetric] = useState<NodeSizeMetric>(DEFAULT_SIZE_METRIC);
  const [colorMode, setColorMode] = useState<GraphColorMode>(DEFAULT_COLOR_MODE);
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('organic');
  const [interClusterMode, setInterClusterMode] = useState<InterClusterMode>(DEFAULT_INTER_CLUSTER_MODE);
  const [showEdgeArrows, setShowEdgeArrows] = useState(true);
  // starts OFF; the isExploreGraph effect below flips it per graph kind
  const [volumeSizedEdges, setVolumeSizedEdges] = useState(false);
  const [graphHighlight, setGraphHighlight] = useState<GraphHighlight | null>(null);
  // remount key for SigmaContainer — bumped on every graph rebuild (see the
  // rebuild effect's finish() for why in-place recreation is unsafe)
  const graphVersionRef = useRef(0);
  // graph id the explore starting config was applied for — reloads of the same
  // graph (WS regenerate-ready refetch) must not clobber the user's tweaks
  const exploreDefaultsAppliedRef = useRef<number | null>(null);
  // interaction types checked in the Filters tab — like every other facet,
  // empty = no filter, checked values narrow the canvas to those edges;
  // deliberately survives graph rebuilds (it's a setting, not a spotlight)
  const [selectedMentionTypes, setSelectedMentionTypes] = useState<string[]>([]);
  // node facet filters (Filters tab): checked values narrow the canvas to
  // matching actors. Like selectedMentionTypes it survives rebuilds (mode / size
  // metric / layout changes), but resets when a new dataset arrives — its values
  // may simply not exist there, and a stale selection would blank the canvas
  const [nodeFilters, setNodeFilters] = useState<NodeFilters>(EMPTY_NODE_FILTERS);
  // "Highlight instead of hide": non-matching actors stay on the canvas as gray
  // context (spotlight treatment) rather than disappearing. A display
  // preference, not a filter — survives rebuilds and the Reset filters button
  const [dimFilteredNodes, setDimFilteredNodes] = useState(false);
  // min interactions per relation (edge totalCount threshold, 0 = off) — an edge
  // filter like the relation-type checkboxes, so it lives beside them, not in
  // nodeFilters
  // z-score buckets the user unchecked for EDGES (uncheck-to-hide, like relation
  // types); only meaningful while z-score coloring is on
  const [hiddenEdgeAnomalyBuckets, setHiddenEdgeAnomalyBuckets] = useState<ZScoreBucket[]>([]);
  // hide non-reciprocal relations — mutual links are a proxy for real relationships
  const [mutualOnly, setMutualOnly] = useState(false);
  // node ids queued for the "Add to actor group" modal; null = modal closed.
  // Node ids ARE source ids (the tooltip links to /sources/{id}), so they go
  // straight into the modal's `sources` prop
  const [addToGroupIds, setAddToGroupIds] = useState<string[] | null>(null);
  // the saved graph row for this route id (result payload stripped into
  // rawResponse/caseGraphResponse below) — drives the title and the
  // generating/failed page states
  const [savedGraph, setSavedGraph] = useState<SavedGraphDetailDTO | null>(null);
  const [rawResponse, setRawResponse] = useState<SocialGraphModesResponse | null>(null);
  const [caseGraphResponse, setCaseGraphResponse] = useState<CaseGraphResponse | null>(null);
  const [activeGraphKind, setActiveGraphKind] = useState<ActiveGraphKind>('mention');
  const [graph, setGraph] = useState<Graph<NodeAttributes, EdgeAttributes> | null>(null);

  // Amplitude tracker pre-bound to the open graph; panels reach it through
  // SocialGraphAnalyticsContext (see analytics.ts for the event list)
  const analyticsBase = useMemo(
    () => ({
      graph_id: savedGraph?.id ?? null,
      graph_kind: savedGraph?.kind ?? null,
      source_type: savedGraph?.source_type ?? null,
      is_explore_graph: Boolean(rawResponse?.explore),
      is_embedded: isEmbedded,
      is_read_only_host: isReadOnlyHost,
    }),
    [savedGraph?.id, savedGraph?.kind, savedGraph?.source_type, rawResponse?.explore, isEmbedded, isReadOnlyHost],
  );
  const track = useSocialGraphTracker(analyticsBase, currentUser);
  // fire-once-per-graph "opened" event (same guard pattern as DashboardDetailPage)
  const trackedGraphIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!savedGraph || trackedGraphIdRef.current === savedGraph.id) return;
    trackedGraphIdRef.current = savedGraph.id;
    track('sg_graph_opened', { status: savedGraph.status });
  }, [savedGraph, track]);
  const [stats, setStats] = useState<GraphStats | null>(null);
  const [loading, setLoading] = useState(true);
  // layout computing in the worker — the old graph stays interactive meanwhile
  const [layoutPending, setLayoutPending] = useState(false);
  const layoutWorkerRef = useRef<Worker | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);

  // Node selection and group (category / community / z-band) selection are
  // mutually exclusive single-selection states — picking one clears the other,
  // and clicking empty canvas (focus null) clears both. The right panel always
  // mirrors whichever is active; closing it IS clearing the selection.
  const selectNode = useCallback((nodeId: string | null) => {
    setFocusedNodeId(nodeId);
    setGraphHighlight(null);
  }, []);
  const selectHighlight = useCallback(
    (highlight: GraphHighlight | null) => {
      setGraphHighlight(highlight);
      setFocusedNodeId(null);
      if (highlight) {
        track('sg_group_spotlighted', { group_type: highlight.type, group_value: String(highlight.value) });
      }
    },
    [track],
  );
  // left panel open state mirrored up from GraphPanel — shifts the stats chips
  // and legend out from under the 20rem panel overlay
  const [leftPanelOpen, setLeftPanelOpen] = useState(false);
  // bottom-left legend (Figma 353-640 collapsed / 353-564 open / 353-594 z-score).
  // Open by default and sticky: only an explicit click on the legend itself
  // closes it — no outside-click / selection / mode-switch auto-close.
  const [legendOpen, setLegendOpen] = useState(true);
  // Graph guide sheet: open flag + the page it shows. The page outlives the
  // sheet on purpose — reopening lands on the page the reader closed it on
  const [guideOpen, setGuideOpen] = useState(false);
  const [guidePage, setGuidePage] = useState<GuidePageId>(DEFAULT_GUIDE_PAGE);
  // section anchor for the current open (hint "Learn more"); not remembered
  const [guideSection, setGuideSection] = useState<string | null>(null);
  const openGuide = useCallback((page?: GuidePageId, section?: string) => {
    if (page) setGuidePage(page);
    setGuideSection(section ?? null);
    setGuideOpen(true);
  }, []);
  const changeGuidePage = useCallback((page: GuidePageId, section?: string) => {
    setGuidePage(page);
    setGuideSection(section ?? null);
  }, []);
  const closeGuide = useCallback(() => setGuideOpen(false), []);
  const guideApi = useMemo(() => ({ openGuide }), [openGuide]);
  const [showKeyConnectors, setShowKeyConnectors] = useState(false);
  // overview-strip window as inclusive timelineData indices; null = whole period.
  // The window zooms the chart AND is the playback range (Figma Timeline / Panel)
  const [timelineWindow, setTimelineWindow] = useState<[number, number] | null>(null);
  // single day picked by clicking a bar (absolute timelineData index); filters
  // the graph while idle — playback plays the window range and ignores it
  const [selectedDayIndex, setSelectedDayIndex] = useState<number | null>(null);
  const [playSpeed, setPlaySpeed] = useState<number>(1);
  // timeline series switched off via the header legend filters
  const [hiddenSeries, setHiddenSeries] = useState<string[]>([]);
  const [hoveredPeriod, setHoveredPeriod] = useState<Date | null>(null);
  const [showTimeline, setShowTimeline] = useState(true);
  // timeline playback: position within playableIndices (not a timelineData index)
  const [playPos, setPlayPos] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  // outside-click boundary for clearing the day selection
  const timelinePanelRef = useRef<HTMLDivElement>(null);
  // fullscreen targets this wrapper (not sigma's own container) so the settings
  // panel, search box, node panel, and timeline stay visible in fullscreen mode
  const graphWrapperRef = useRef<HTMLDivElement>(null);
  // mirrors the live sigma instance (see SigmaRefSync) so GraphHeader's export
  // buttons, which sit outside SigmaContainer, can reach it
  const sigmaRef = useRef<Sigma<NodeAttributes, EdgeAttributes> | null>(null);

  // WebGL context loss (browser context cap / GPU eviction of a background
  // tab — see WebGLContextLossRecovery) leaves sigma drawing into nothing. The
  // only way back is a fresh Sigma instance: bump the container key and
  // re-render, carrying the camera over so the view doesn't jump.
  const [, bumpContainerRemount] = useReducer((n: number) => n + 1, 0);
  const contextLossCameraRef = useRef<CameraState | null>(null);
  // remount only while visible: a hidden tab that recreates its contexts can
  // evict the visible tab's, which would recreate and evict back — ping-pong
  const remountWhenVisibleRef = useRef(false);
  // and never more than a few times in a row: if the browser refuses to keep a
  // context alive, retrying every frame would only make things worse
  const contextLossTimesRef = useRef<number[]>([]);

  const remountAfterContextLoss = useCallback(() => {
    const now = Date.now();
    contextLossTimesRef.current = contextLossTimesRef.current.filter((at) => now - at < CONTEXT_LOSS_WINDOW_MS);
    if (contextLossTimesRef.current.length >= CONTEXT_LOSS_MAX_RECOVERIES) return;
    contextLossTimesRef.current.push(now);
    graphVersionRef.current += 1;
    bumpContainerRemount();
  }, []);

  const handleContextLost = useCallback(
    (camera: CameraState) => {
      contextLossCameraRef.current = camera;
      if (document.visibilityState === 'visible') remountAfterContextLoss();
      else remountWhenVisibleRef.current = true;
    },
    [remountAfterContextLoss]
  );

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || !remountWhenVisibleRef.current) return;
      remountWhenVisibleRef.current = false;
      remountAfterContextLoss();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [remountAfterContextLoss]);

  const handleExportPng = useCallback(() => {
    if (!sigmaRef.current) return;
    exportGraphAsPng(sigmaRef.current, `social-graph-${new Date().toISOString().slice(0, 10)}.png`);
    track('sg_graph_exported', { format: 'png' });
  }, [track]);

  const handleExportSvg = useCallback(() => {
    if (!sigmaRef.current) return;
    exportGraphAsSvg(sigmaRef.current, `social-graph-${new Date().toISOString().slice(0, 10)}.svg`);
    track('sg_graph_exported', { format: 'svg' });
  }, [track]);

  // The render below redirects flag-less users away, but effects and WS
  // handlers still fire before the redirect settles — and every social-graph
  // endpoint (reports included) answers 403 without the flag, which API.fetch
  // treats as an expired session (logout). No request may leave unguarded.
  const hasSocialGraphFlag = Boolean(
    currentUser?.workspace?.config?.featureflags?.includes('social_graph'),
  );

  // single-actor explore graphs are exploratory-only: their zscore payload is an
  // empty shell and the backend rejects report requests — hide both the Z-Score
  // mode toggle and the report entry for them
  const isExploreGraph = Boolean(rawResponse?.explore);

  // ——— Z-score PDF report (backend PR #765; actor graphs only) ———
  // 'none' hides the header button (case/explore graphs / no z-score data),
  // 'empty' disables it with an explanation, 'available' wires it up
  const zscoreReportAvailability = useMemo<'none' | 'empty' | 'available'>(() => {
    // ZSCORE_ENABLED kill-switch: no report while the calculation is known-wrong
    if (isReadOnlyHost || !ZSCORE_ENABLED || activeGraphKind !== 'mention' || !rawResponse?.zscore || isExploreGraph) {
      return 'none';
    }
    return rawResponse.zscore.nodes.length > 0 ? 'available' : 'empty';
  }, [isReadOnlyHost, activeGraphKind, rawResponse, isExploreGraph]);

  // true from the request until the WS/poll reports a terminal status
  const [reportBusy, setReportBusy] = useState(false);

  const downloadReport = useCallback(async () => {
    if (!graphId || !hasSocialGraphFlag) return;
    // presigned link with an attachment content-disposition — navigating the
    // anchor triggers the browser download without leaving the page
    const url = await socialGraphApi.getReportDownloadLink(Number(graphId));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.rel = 'noopener';
    anchor.click();
  }, [graphId, hasSocialGraphFlag]);

  // shared by the WS handler and the fallback poll
  const applyReportStatus = useCallback(
    (report: Pick<SocialGraphReportDTO, 'status'>) => {
      if (report.status === 'ready') {
        setReportBusy(false);
        toast.success(t('Z-score report is ready.') as string);
        downloadReport().catch((error) => showRequestError(error, t('Z-score report')));
      } else if (report.status === 'failed') {
        setReportBusy(false);
        toast.error(t('Z-score report failed to generate.') as string);
      }
    },
    [downloadReport, t],
  );

  const handleZScoreReport = useCallback(async () => {
    if (!graphId) return;
    const lang: SocialGraphReportLang = i18n.language?.startsWith('en') ? 'EN' : 'UA';
    track('sg_zscore_report_requested', { lang });
    try {
      setReportBusy(true);
      const report = await socialGraphApi.requestReport(Number(graphId), lang);
      // idempotent: a report already generated in this language downloads right away
      if (report.status === 'ready' && report.lang === lang) {
        setReportBusy(false);
        await downloadReport();
      } else {
        toast.info(t('Report generation started. It will download automatically when ready.') as string);
      }
    } catch (error) {
      setReportBusy(false);
      showRequestError(error, t('Z-score report'));
    }
  }, [graphId, i18n.language, downloadReport, t, track]);

  // the worker pushes report transitions to the creator personally
  useEffect(() => {
    if (!graphId) return undefined;
    const handleReportUpdate = (message: { data?: SocialGraphReportDTO }) => {
      const data = message?.data;
      if (!data || data.graph_id !== Number(graphId)) return;
      applyReportStatus(data);
    };
    websocketConnection.addHandler('SOCIAL_GRAPH_REPORT', handleReportUpdate);
    return () => websocketConnection.removeHandler('SOCIAL_GRAPH_REPORT', handleReportUpdate);
  }, [graphId, applyReportStatus]);

  // WS is best-effort — slow-poll the report while one is in flight. The flag
  // guard covers runtime revocation mid-generation (403 would log the user out)
  useEffect(() => {
    if (!reportBusy || !graphId || !hasSocialGraphFlag) return undefined;
    const interval = window.setInterval(() => {
      socialGraphApi
        .getReport(Number(graphId))
        .then(applyReportStatus)
        // swallowed deliberately: this is a safety net behind the WS push, an
        // intermittent poll failure shouldn't toast
        .catch(() => undefined);
    }, 15000);
    return () => window.clearInterval(interval);
  }, [reportBusy, graphId, hasSocialGraphFlag, applyReportStatus]);

  const effectiveSizeMetric = useMemo<NodeSizeMetric>(() => {
    // case graphs only carry betweenness + degree
    if (activeGraphKind === 'case' && sizeMetric !== 'betweenness' && sizeMetric !== 'degree') {
      return 'degree';
    }
    // anomaly scores are only populated in z-score analysis
    if (sizeMetric === 'anomaly' && mode !== 'zscore') {
      return 'degree';
    }
    return sizeMetric;
  }, [activeGraphKind, sizeMetric, mode]);

  const currentModeData = useMemo(
    () => rawResponse ? rawResponse[mode] : null,
    [rawResponse, mode],
  );

  // in z-score analysis edge color encodes interaction anomaly instead of the
  // structural community/mention schemes (case graphs have no z-scores)
  const zscoreEdgeColoring = activeGraphKind === 'mention' && mode === 'zscore';

  // the edge-centric schemes are hidden from the color-scheme select while
  // z-score coloring owns the edges — render the default instead, without
  // touching the stored choice so it comes back when z-score is left
  // (same derived pattern as effectiveSizeMetric)
  // "By actor group" (scope) and "By connection" (internal / external) only mean
  // something on actor-group graphs: case graphs have no scope boundary (every
  // edge is internal) and an explore graph's in-scope flag marks the explored
  // actor alone, so neither split exists there. Derived rather than reset so the
  // stored choice survives a switch between graphs (same rule as the z-score
  // exemptions).
  const isActorGroupGraph = activeGraphKind === 'mention' && !isExploreGraph;
  const effectiveColorMode =
    (zscoreEdgeColoring && (colorMode === 'mention' || colorMode === 'connection')) ||
    ((colorMode === 'scope' || colorMode === 'connection') && !isActorGroupGraph)
      ? DEFAULT_COLOR_MODE
      : colorMode;

  // a response has arrived but the graph may still be building (the layout effect
  // runs after render, and FA2 takes a while) — the empty state must keep showing
  // "Loading" in that window instead of flashing "No relations found"
  const hasResponse = activeGraphKind === 'case' ? caseGraphResponse !== null : currentModeData !== null;

  const timelineData = useMemo(() => {
    if (activeGraphKind === 'case' && caseGraphResponse) {
      return aggregateCaseTimeline(caseGraphResponse.edges);
    }
    return currentModeData ? aggregateTimeline(currentModeData.edges) : [];
  }, [activeGraphKind, caseGraphResponse, currentModeData]);

  // clamped to the data so stale indices never read out of bounds
  const [windowStartIdx, windowEndIdx] = useMemo<[number, number]>(() => {
    const last = Math.max(0, timelineData.length - 1);
    if (!timelineWindow) return [0, last];
    return [Math.min(timelineWindow[0], last), Math.min(timelineWindow[1], last)];
  }, [timelineData.length, timelineWindow]);

  // the chart is zoomed to the strip window; the strip itself always shows the
  // whole period
  const windowedTimelineData = useMemo(
    () => timelineData.slice(windowStartIdx, windowEndIdx + 1),
    [timelineData, windowStartIdx, windowEndIdx],
  );

  const timelineSeriesTypes = useMemo(() => {
    const present = new Set<string>();
    for (const point of timelineData) {
      for (const type of Object.keys(point.byType)) present.add(type);
    }
    return TIMELINE_SERIES_ORDER.filter((type) => present.has(type));
  }, [timelineData]);

  const visibleSeriesTypes = useMemo(
    () => timelineSeriesTypes.filter((type) => !hiddenSeries.includes(type)),
    [timelineSeriesTypes, hiddenSeries],
  );

  const windowEdgeKeys = useMemo(() => {
    if (!timelineWindow || timelineData.length === 0) return null;
    const range: [Date, Date] = [timelineData[windowStartIdx].date, timelineData[windowEndIdx].date];
    if (activeGraphKind === 'case' && caseGraphResponse) {
      return getCaseActiveEdgeKeys(caseGraphResponse.edges, range);
    }
    return currentModeData ? getActiveEdgeKeys(currentModeData.edges, range) : null;
  }, [activeGraphKind, caseGraphResponse, currentModeData, timelineWindow, timelineData, windowStartIdx, windowEndIdx]);

  const selectedDayDate = selectedDayIndex !== null ? timelineData[selectedDayIndex]?.date ?? null : null;

  const dayEdgeKeys = useMemo(() => {
    if (!selectedDayDate) return null;
    if (activeGraphKind === 'case' && caseGraphResponse) {
      return getCaseEdgeKeysForPeriod(caseGraphResponse.edges, selectedDayDate);
    }
    return currentModeData ? getEdgeKeysForPeriod(currentModeData.edges, selectedDayDate) : null;
  }, [activeGraphKind, caseGraphResponse, currentModeData, selectedDayDate]);

  const hoverEdgeKeys = useMemo(() => {
    if (!hoveredPeriod) return null;
    if (activeGraphKind === 'case' && caseGraphResponse) {
      return getCaseEdgeKeysForPeriod(caseGraphResponse.edges, hoveredPeriod);
    }
    return currentModeData ? getEdgeKeysForPeriod(currentModeData.edges, hoveredPeriod) : null;
  }, [activeGraphKind, caseGraphResponse, currentModeData, hoveredPeriod]);

  // playback steps through these timelineData indices — exactly the buckets
  // inside the overview-strip window
  const playableIndices = useMemo(() => {
    const indices: number[] = [];
    for (let i = windowStartIdx; i <= windowEndIdx && i < timelineData.length; i++) {
      indices.push(i);
    }
    return indices;
  }, [timelineData.length, windowStartIdx, windowEndIdx]);

  const playIndex = playPos !== null ? playableIndices[playPos] ?? null : null;
  const playPeriod = playIndex !== null ? timelineData[playIndex]?.date ?? null : null;

  // cumulative reveal: each frame shows everything active from the start of the
  // playable window up to the current bucket, so the network grows as time plays
  const playEdgeKeys = useMemo(() => {
    if (!playPeriod) return null;
    const firstIndex = playableIndices[0];
    const windowStart = firstIndex !== undefined ? timelineData[firstIndex].date : playPeriod;
    const range: [Date, Date] = [windowStart, playPeriod];
    if (activeGraphKind === 'case' && caseGraphResponse) {
      return getCaseActiveEdgeKeys(caseGraphResponse.edges, range);
    }
    return currentModeData ? getActiveEdgeKeys(currentModeData.edges, range) : null;
  }, [activeGraphKind, caseGraphResponse, currentModeData, playPeriod, playableIndices, timelineData]);

  // manual hover > playback frame > selected day > strip window. When a range
  // is chosen on the strip, playback plays that range and ignores the day.
  const activeEdgeKeys = hoverEdgeKeys ?? playEdgeKeys ?? dayEdgeKeys ?? windowEdgeKeys;

  const brushNodeIds = useMemo(
    () => (windowEdgeKeys ? getActiveNodeIds(windowEdgeKeys) : null),
    [windowEdgeKeys],
  );

  const activeNodeIds = useMemo(
    () => (activeEdgeKeys ? getActiveNodeIds(activeEdgeKeys) : null),
    [activeEdgeKeys],
  );

  // adaptive playback cadence (Figma Timeline / Speed note): the visible window
  // plays end to end in about 30s at 1×, clamped to 2–20 bars per second
  const playIntervalMs = useMemo(() => {
    const barsPerSecond = Math.min(20, Math.max(2, playableIndices.length / 30));
    return 1000 / (barsPerSecond * playSpeed);
  }, [playableIndices.length, playSpeed]);

  // advance the playback frame while playing
  useEffect(() => {
    if (!isPlaying) return;
    const id = setInterval(() => {
      setPlayPos((prev) => (prev === null ? 0 : prev + 1));
    }, playIntervalMs);
    return () => clearInterval(id);
  }, [isPlaying, playIntervalMs]);

  // stop and reset once playback runs past the last playable bucket (or the
  // playable window shrinks under the current position)
  useEffect(() => {
    if (playPos !== null && playPos >= playableIndices.length) {
      setIsPlaying(false);
      setPlayPos(null);
    }
  }, [playPos, playableIndices.length]);

  // a new dataset invalidates the playback frame and every index-based selection
  useEffect(() => {
    setIsPlaying(false);
    setPlayPos(null);
    setTimelineWindow(null);
    setSelectedDayIndex(null);
    setHiddenSeries([]);
  }, [timelineData]);

  // shrinking the window away from the selected day drops the selection
  useEffect(() => {
    if (selectedDayIndex !== null && (selectedDayIndex < windowStartIdx || selectedDayIndex > windowEndIdx)) {
      setSelectedDayIndex(null);
    }
  }, [selectedDayIndex, windowStartIdx, windowEndIdx]);

  // designer spec: the day selection clears in exactly two ways — Reset, or a
  // click anywhere outside the timeline panel (header controls stay inside)
  useEffect(() => {
    if (selectedDayIndex === null) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (!timelinePanelRef.current?.contains(event.target as Node)) {
        setSelectedDayIndex(null);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [selectedDayIndex]);

  const handlePlayToggle = () => {
    if (isPlaying) {
      setIsPlaying(false);
      track('sg_timeline_used', { action: 'pause' });
      return;
    }
    track('sg_timeline_used', { action: 'play', speed: playSpeed });
    // restart from the first frame unless paused mid-run
    if (playPos === null || playPos >= playableIndices.length) {
      setPlayPos(0);
    }
    setIsPlaying(true);
  };

  const handleWindowChange = useCallback((next: [number, number]) => {
    // a changed window invalidates the current playback frame (the strip is
    // pointer-locked while playing, but its sliders stay keyboard-reachable)
    setIsPlaying(false);
    setPlayPos(null);
    // dragging back to the full extent clears the time filter entirely
    const isFullExtent = next[0] === 0 && next[1] >= timelineData.length - 1;
    setTimelineWindow(isFullExtent ? null : next);
    track('sg_timeline_used', { action: 'range_changed', full_extent: isFullExtent });
  }, [timelineData.length, track]);

  // the chart reports indices relative to its windowed slice
  const handleSelectDay = useCallback((relIndex: number) => {
    setSelectedDayIndex(windowStartIdx + relIndex);
    track('sg_timeline_used', { action: 'day_selected' });
  }, [windowStartIdx, track]);

  const stepSelectedDay = (delta: -1 | 1) => {
    setSelectedDayIndex((prev) =>
      prev === null ? null : Math.min(windowEndIdx, Math.max(windowStartIdx, prev + delta)),
    );
  };

  const toggleTimelineSeries = (type: string) => {
    setHiddenSeries((prev) =>
      prev.includes(type) ? prev.filter((item) => item !== type) : [...prev, type],
    );
  };

  // back to the defaults: whole period, no day. Deliberately does not touch
  // playback — the Reset control stays live while the animation runs.
  const handleTimelineReset = () => {
    setTimelineWindow(null);
    setSelectedDayIndex(null);
    track('sg_timeline_used', { action: 'reset' });
  };

  // header shows the playhead while playing, the selected day otherwise
  const playheadLabel = playIndex !== null ? timelineData[playIndex]?.label ?? null : null;
  const selectedDayLabel = selectedDayIndex !== null ? timelineData[selectedDayIndex]?.label ?? null : null;
  const timelineDayLabel = playheadLabel ?? selectedDayLabel;
  const timelineRangeLabel = windowedTimelineData.length
    ? `${windowedTimelineData[0].label} – ${windowedTimelineData[windowedTimelineData.length - 1].label}`
    : '';
  const timelineWindowEmpty = windowedTimelineData.every((point) =>
    visibleSeriesTypes.every((type) => (point.byType[type] ?? 0) === 0),
  );
  const timelineAtDefaults = timelineWindow === null && selectedDayIndex === null;

  const selectedNodeInfo = useMemo<SelectedNodeInfo | null>(() => {
    if (!focusedNodeId || !graph || !graph.hasNode(focusedNodeId)) return null;
    const attrs = graph.getNodeAttributes(focusedNodeId);
    const mentionCounts: Record<string, number> = {};
    graph.forEachEdge(focusedNodeId, (_edge, edgeAttrs) => {
      for (const [type, count] of Object.entries(edgeAttrs.mentionTypes)) {
        mentionCounts[type] = (mentionCounts[type] ?? 0) + count;
      }
    });
    return {
      nodeId: focusedNodeId,
      label: attrs.label,
      inEvent: attrs.inEvent,
      community: attrs.community,
      betweenness: attrs.betweenness,
      pagerank: attrs.pagerank,
      hubScore: attrs.hubScore,
      authorityScore: attrs.authorityScore,
      actorRole: attrs.actorRole,
      platform: attrs.platform,
      sourceUrl: attrs.sourceUrl,
      connections: actorConnectionStats(graph, focusedNodeId),
      mentionCounts,
      isKeyConnector: attrs.isKeyConnector,
      isHighRisk: attrs.isHighRisk,
      isStateAffiliated: attrs.isStateAffiliated,
      isHighInteraction: attrs.isHighInteraction,
      discreditations: attrs.discreditations,
      affiliations: attrs.affiliations,
      inauthenticTypes: attrs.inauthenticTypes,
      tags: attrs.tags,
      isVerified: attrs.isVerified,
      originCountry: attrs.originCountry,
      audience: attrs.audience,
      reciprocity: attrs.reciprocity,
      clusteringCoeff: attrs.clusteringCoeff,
      platformDiversity: attrs.platformDiversity,
      bridgeScore: attrs.bridgeScore,
      weightGini: attrs.weightGini,
      avgZScore: attrs.avgZScore,
      weightedAnomalyScore: attrs.weightedAnomalyScore,
    };
  }, [focusedNodeId, graph]);

  // the selected actor's neighbors for the panel's Connections tab: one entry
  // per neighbor with direction relative to the selection; in z-score analysis
  // an edge with an anomalous z (either direction) flags the whole connection.
  // Sorted anomalous-first by z, then by interaction volume
  const selectedConnections = useMemo<NodeConnection[]>(() => {
    if (!focusedNodeId || !graph || !graph.hasNode(focusedNodeId)) return [];
    const byNeighbor = new Map<string, NodeConnection>();
    graph.forEachEdge(focusedNodeId, (_edge, attrs, source, target) => {
      const neighborId = source === focusedNodeId ? target : source;
      if (neighborId === focusedNodeId) return;
      const direction = source === focusedNodeId ? 'out' : 'in';
      const zScore = zscoreEdgeColoring ? attrs.zScore : null;
      const anomalous = zscoreEdgeColoring && zscoreEdgeStyle(attrs.zScore).anomalous;
      const existing = byNeighbor.get(neighborId);
      if (!existing) {
        const neighborAttrs = graph.getNodeAttributes(neighborId);
        byNeighbor.set(neighborId, {
          id: neighborId,
          label: neighborAttrs.label,
          platform: neighborAttrs.platform,
          direction,
          zScore,
          anomalous,
          strength: attrs.totalCount,
          inboundCount: direction === 'in' ? attrs.totalCount : 0,
          outboundCount: direction === 'out' ? attrs.totalCount : 0,
        });
        return;
      }
      if (existing.direction !== direction) existing.direction = 'mutual';
      if (zScore !== null && (existing.zScore === null || zScore > existing.zScore)) {
        existing.zScore = zScore;
      }
      existing.anomalous = existing.anomalous || anomalous;
      existing.strength += attrs.totalCount;
      if (direction === 'in') existing.inboundCount += attrs.totalCount;
      else existing.outboundCount += attrs.totalCount;
    });
    return Array.from(byNeighbor.values()).sort(
      (a, b) =>
        Number(b.anomalous) - Number(a.anomalous) ||
        (b.zScore ?? 0) - (a.zScore ?? 0) ||
        b.strength - a.strength ||
        a.label.localeCompare(b.label),
    );
  }, [focusedNodeId, graph, zscoreEdgeColoring]);

  // size + density per community, computed once per graph build so selecting a
  // node is a map lookup instead of a full graph scan per click
  const communityStatsMap = useMemo(() => {
    const stats = new Map<number, { size: number; density: number }>();
    if (!graph) return stats;
    const sizes = new Map<number, number>();
    graph.forEachNode((_id, attrs) => {
      sizes.set(attrs.community, (sizes.get(attrs.community) ?? 0) + 1);
    });
    const intraEdges = new Map<number, number>();
    graph.forEachEdge((_edge, _attrs, _source, _target, sourceAttrs, targetAttrs) => {
      if (sourceAttrs.community === targetAttrs.community) {
        intraEdges.set(sourceAttrs.community, (intraEdges.get(sourceAttrs.community) ?? 0) + 1);
      }
    });
    sizes.forEach((size, community) => {
      const edgeCount = intraEdges.get(community) ?? 0;
      stats.set(community, { size, density: size > 1 ? edgeCount / (size * (size - 1)) : 0 });
    });
    return stats;
  }, [graph]);

  // the selected node's community card data
  const selectedCommunityStats = selectedNodeInfo
    ? communityStatsMap.get(selectedNodeInfo.community) ?? null
    : null;

  // actors grouped by role, largest first — data for the panel's Actors tab
  const roleGroups = useMemo(() => {
    if (!graph) return [];
    const groups = new Map<string, { id: string; label: string; platform: string; size: number }[]>();
    graph.forEachNode((id, attrs) => {
      const list = groups.get(attrs.actorRole);
      const entry = { id, label: attrs.label, platform: attrs.platform, size: attrs.size };
      if (list) list.push(entry);
      else groups.set(attrs.actorRole, [entry]);
    });
    return Array.from(groups.entries())
      .map(([role, members]) => ({ role, members: members.sort((a, b) => b.size - a.size) }))
      .sort((a, b) => b.members.length - a.members.length);
  }, [graph]);

  // actors grouped by z-score anomaly bucket, most severe first — empty when the
  // dataset has no per-node z-scores (case graphs, exploratory mode), which hides
  // the grouping switch in the Actors tab
  const zscoreGroups = useMemo(() => {
    // kill-switch guards against exploratory payloads that carry avg z-scores —
    // no Z-Score anomaly section while the calculation is known-wrong
    if (!ZSCORE_ENABLED || !graph) return [];
    const groups = new Map<ZScoreBucket, { id: string; label: string; platform: string; z: number }[]>();
    graph.forEachNode((id, attrs) => {
      if (attrs.zscoreBucket === null || attrs.avgZScore === null) return;
      const entry = { id, label: attrs.label, platform: attrs.platform, z: attrs.avgZScore };
      const list = groups.get(attrs.zscoreBucket);
      if (list) list.push(entry);
      else groups.set(attrs.zscoreBucket, [entry]);
    });
    return ZSCORE_BUCKETS.filter((bucket) => groups.has(bucket)).map((bucket) => ({
      bucket,
      members: groups.get(bucket)!.sort((a, b) => b.z - a.z),
    }));
  }, [graph]);

  // one event per selection — selectedNodeInfo also recomputes on recolor /
  // rebuild, so key on the node id rather than the memo identity
  const trackedActorRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedNodeInfo) {
      trackedActorRef.current = null;
      return;
    }
    if (trackedActorRef.current === selectedNodeInfo.nodeId) return;
    trackedActorRef.current = selectedNodeInfo.nodeId;
    track('sg_actor_selected', { platform: selectedNodeInfo.platform, in_actor_group: selectedNodeInfo.inEvent });
  }, [selectedNodeInfo, track]);

  const openAddToGroup = useCallback(
    (sourceIds: string[]) => {
      if (sourceIds.length === 0) return;
      setAddToGroupIds(sourceIds);
      track('sg_add_to_group_opened', { via: 'actors', actor_count: sourceIds.length });
    },
    [track],
  );

  // "copy whole cluster": resolve a community id to its member node ids
  const addClusterToGroup = useCallback(
    (clusterId: number) => {
      if (!graph) return;
      const ids: string[] = [];
      graph.forEachNode((id, attrs) => {
        if (attrs.community === clusterId) ids.push(id);
      });
      if (ids.length === 0) return;
      setAddToGroupIds(ids);
      track('sg_add_to_group_opened', { via: 'community', actor_count: ids.length, community_id: clusterId });
    },
    [graph, track],
  );

  // boolean node categories (high risk, state affiliated, critical bridges,
  // high-interaction) with members — clickable spotlight groups at the top of
  // the Actors tab; categories can overlap, so a node may appear in several.
  // Members sort by node size, except the High-Interaction category which the
  // PM wants ordered by interaction volume, most first (2026-10-15)
  const flagGroups = useMemo<FlagGroup[]>(() => {
    if (!graph) return [];
    const groups: Record<NodeFlag, FlagGroup['members']> = {
      highRisk: [],
      stateAffiliated: [],
      connector: [],
      highInteraction: [],
    };
    graph.forEachNode((id, attrs) => {
      const entry = {
        id,
        label: attrs.label,
        platform: attrs.platform,
        size: attrs.size,
        interactions: attrs.interactions,
      };
      if (attrs.isHighRisk) groups.highRisk.push(entry);
      if (attrs.isStateAffiliated) groups.stateAffiliated.push(entry);
      if (attrs.isKeyConnector) groups.connector.push(entry);
      if (attrs.isHighInteraction) groups.highInteraction.push(entry);
    });
    return (Object.keys(groups) as NodeFlag[])
      .filter((flag) => groups[flag].length > 0)
      .map((flag) => ({
        flag,
        members: groups[flag].sort((a, b) =>
          flag === 'highInteraction' ? b.interactions - a.interactions : b.size - a.size,
        ),
      }));
  }, [graph]);

  // dominant mention types present in the graph with edge counts — drives the
  // relation-type filter checkboxes (mention graphs only; case graphs are all REPOST)
  const mentionTypes = useMemo(() => {
    if (!graph || activeGraphKind !== 'mention') return [];
    const counts = new Map<string, number>();
    graph.forEachEdge((_edge, attrs) => {
      counts.set(attrs.dominantMentionType, (counts.get(attrs.dominantMentionType) ?? 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count);
  }, [graph, activeGraphKind]);

  const toggleMentionType = useCallback(
    (type: string) => {
      const selected = !selectedMentionTypes.includes(type);
      setSelectedMentionTypes((prev) =>
        prev.includes(type) ? prev.filter((item) => item !== type) : [...prev, type],
      );
      track('sg_relation_type_filter_toggled', { relation_type: type, selected });
    },
    [selectedMentionTypes, track],
  );

  // a new dataset invalidates the facet selection (see the state comment)
  useEffect(() => {
    setNodeFilters(EMPTY_NODE_FILTERS);
    setHiddenEdgeAnomalyBuckets([]);
    setMutualOnly(false);
  }, [rawResponse, caseGraphResponse]);

  // leaving z-score analysis nulls every node's bucket and avg z-score, so those
  // selections would hide the whole graph — drop just them, keep the rest; the
  // edge-anomaly filter goes inert outside z-score, clear it too so Reset/active
  // state stays truthful
  useEffect(() => {
    if (!zscoreEdgeColoring) {
      setNodeFilters((prev) =>
        prev.zscoreBuckets.length > 0 || prev.minAvgZScore !== null
          ? { ...prev, zscoreBuckets: [], minAvgZScore: null }
          : prev,
      );
      setHiddenEdgeAnomalyBuckets((prev) => (prev.length > 0 ? [] : prev));
    }
  }, [zscoreEdgeColoring]);

  // facet values present in the current graph with actor counts — drives the
  // Filters tab's node sections; a section with no values simply doesn't render
  // (graphs generated before backend PR #823 carry no affiliation/inauthentic
  // data, mention graphs carry no origin/audience profile fields)
  // i18n.language is a dep only to re-derive tag labels on a language switch —
  // isUkLocale() reads the i18next locale from localStorage
  const nodeFilterOptions = useMemo(() => {
    const scopes = new Map<string, number>();
    const communities = new Map<string, number>();
    const tags = new Map<string, { count: number; nameEn: string; nameUk: string }>();
    const affiliationTypes = new Map<string, number>();
    const affiliationCountries = new Map<string, number>();
    const discreditations = new Map<string, number>();
    const inauthenticTypes = new Map<string, number>();
    const originCountries = new Map<string, number>();
    const audienceBuckets = new Map<string, number>();
    const bump = (counts: Map<string, number>, key: string) => counts.set(key, (counts.get(key) ?? 0) + 1);
    graph?.forEachNode((_id, attrs) => {
      bump(scopes, attrs.inEvent ? 'in_scope' : 'external');
      bump(communities, String(attrs.community));
      attrs.tags.forEach((tag) => {
        const entry = tags.get(tag.tag);
        if (entry) entry.count += 1;
        else tags.set(tag.tag, { count: 1, nameEn: tag.nameEn, nameUk: tag.nameUk });
      });
      attrs.affiliationTypes.forEach((type) => bump(affiliationTypes, type));
      attrs.affiliationCountries.forEach((country) => bump(affiliationCountries, country));
      attrs.discreditations.forEach((type) => bump(discreditations, type));
      attrs.inauthenticTypes.forEach((type) => bump(inauthenticTypes, type));
      if (attrs.originCountry !== null) bump(originCountries, attrs.originCountry);
      if (attrs.audienceBucket !== null) bump(audienceBuckets, attrs.audienceBucket);
    });
    const toOptions = (counts: Map<string, number>): NodeFilterOption[] =>
      Array.from(counts.entries())
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => b.count - a.count);
    // fixed orders where the values have an inherent one (size buckets, in / out)
    const ordered = (order: string[], counts: Map<string, number>): NodeFilterOption[] =>
      order.filter((value) => counts.has(value)).map((value) => ({ value, count: counts.get(value)! }));
    const useUkNames = isUkLocale();
    return {
      // in / out of the actor group only exists on actor-group graphs — a case
      // graph is all in scope and an explore graph's in_scope flag marks the
      // explored actor, not a group (same rule as the "By actor group" scheme)
      scopes: isActorGroupGraph ? ordered(['in_scope', 'external'], scopes) : [],
      communities: toOptions(communities),
      tags: Array.from(tags.entries())
        .map(([value, { count, nameEn, nameUk }]) => ({
          value,
          count,
          label: (useUkNames ? nameUk || nameEn : nameEn || nameUk) || value,
        }))
        .sort((a, b) => b.count - a.count),
      affiliationTypes: toOptions(affiliationTypes),
      affiliationCountries: toOptions(affiliationCountries),
      discreditations: toOptions(discreditations),
      inauthenticTypes: toOptions(inauthenticTypes),
      originCountries: toOptions(originCountries),
      audienceBuckets: ordered(AUDIENCE_BUCKET_ORDER, audienceBuckets),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph, isActorGroupGraph, i18n.language]);

  // upper bound of the "Minimum connections" input (0 = no edges → no input)
  const maxDegree = useMemo(() => {
    let max = 0;
    graph?.forEachNode((_id, attrs) => {
      if (attrs.degree > max) max = attrs.degree;
    });
    return max;
  }, [graph]);

  // edge z-score buckets present with counts — the edge-anomaly filter's rows
  // (z-score coloring only; ordered most severe first like the legend)
  const edgeAnomalyOptions = useMemo(() => {
    if (!graph || !zscoreEdgeColoring) return [];
    const counts = new Map<ZScoreBucket, number>();
    graph.forEachEdge((_edge, attrs) => {
      const bucket = zscoreNodeBucket(attrs.zScore);
      counts.set(bucket, (counts.get(bucket) ?? 0) + 1);
    });
    return ZSCORE_BUCKETS.filter((bucket) => counts.has(bucket)).map((bucket) => ({
      bucket,
      count: counts.get(bucket)!,
    }));
  }, [graph, zscoreEdgeColoring]);

  // gates the "Mutual relations only" switch — pointless when nothing is mutual
  const reciprocalCount = useMemo(() => {
    let count = 0;
    graph?.forEachEdge((_edge, attrs) => {
      if (attrs.reciprocal) count += 1;
    });
    return count;
  }, [graph]);

  // THE node-visibility predicate — every consumer (hide effect, search, count,
  // focus guard) goes through this so the definition can't drift apart
  // (facets + minDegree + minAvgZScore).
  const isNodeVisible = useCallback(
    (attrs: NodeAttributes): boolean => matchesNodeFilters(attrs, nodeFilters),
    [nodeFilters],
  );

  const toggleEdgeAnomalyBucket = useCallback((bucket: ZScoreBucket) => {
    setHiddenEdgeAnomalyBuckets((prev) =>
      prev.includes(bucket) ? prev.filter((item) => item !== bucket) : [...prev, bucket],
    );
  }, []);

  const setMinAvgZScore = useCallback((value: number | null) => {
    setNodeFilters((prev) => ({ ...prev, minAvgZScore: value }));
  }, []);

  const toggleNodeFilter = useCallback((category: NodeFilterCategory, value: string) => {
    setNodeFilters((prev) => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter((item) => item !== value)
        : [...prev[category], value],
    }));
  }, []);

  const setMinDegree = useCallback((value: number) => {
    setNodeFilters((prev) => (prev.minDegree === value ? prev : { ...prev, minDegree: value }));
  }, []);

  // the Filters tab's Reset clears every filter kind — one mental model
  const resetFilters = useCallback(() => {
    setNodeFilters(EMPTY_NODE_FILTERS);
    setSelectedMentionTypes([]);
    setHiddenEdgeAnomalyBuckets([]);
    setMutualOnly(false);
  }, []);

  // every explicit connection filter — interaction type, mutual-only, anomaly
  // bucket. An edge failing any of them leaves the canvas unconditionally (no
  // anomaly/scope exemptions), and the hover reducer keeps it hidden
  const hasEdgeFilters =
    selectedMentionTypes.length > 0 || mutualOnly || (zscoreEdgeColoring && hiddenEdgeAnomalyBuckets.length > 0);
  const isEdgeFiltered = useCallback(
    (edgeAttrs: EdgeAttributes): boolean =>
      (selectedMentionTypes.length > 0 && !selectedMentionTypes.includes(edgeAttrs.dominantMentionType)) ||
      (mutualOnly && !edgeAttrs.reciprocal) ||
      (zscoreEdgeColoring &&
        hiddenEdgeAnomalyBuckets.length > 0 &&
        hiddenEdgeAnomalyBuckets.includes(zscoreNodeBucket(edgeAttrs.zScore))),
    [selectedMentionTypes, mutualOnly, zscoreEdgeColoring, hiddenEdgeAnomalyBuckets],
  );

  // Filters always narrow the ACTORS, not just the lines (PM rule 2026-10-16):
  // an actor fails the filters when it misses a facet, or when a connection
  // filter removes every one of its connections (it then has nothing left to
  // say on the canvas — the old treatment left such orphans in place, muted).
  // One set drives the canvas effect, the stats chip, the Actors tab and
  // search, so every surface agrees on who is out. What "out" looks like is a
  // separate display choice: hidden by default, dimmed with "Dim instead of hide"
  const filteredOutNodeIds = useMemo<Set<string>>(() => {
    const ids = new Set<string>();
    if (!graph) return ids;
    const hasNodeFilter = hasActiveNodeFilters(nodeFilters);
    if (!hasNodeFilter && !hasEdgeFilters) return ids;
    graph.forEachNode((id, attrs) => {
      if (hasNodeFilter && !isNodeVisible(attrs)) {
        ids.add(id);
        return;
      }
      if (hasEdgeFilters) {
        let hasVisibleEdge = false;
        graph.forEachEdge(id, (_edge, edgeAttrs) => {
          if (!isEdgeFiltered(edgeAttrs)) hasVisibleEdge = true;
        });
        if (!hasVisibleEdge) ids.add(id);
      }
    });
    return ids;
  }, [graph, nodeFilters, hasEdgeFilters, isNodeVisible, isEdgeFiltered]);

  // filtered-out actors leave the canvas only in hide mode; in dim mode they
  // stay put (dimmed), so search/pin may still target them
  const filtersHideNodes = !dimFilteredNodes && filteredOutNodeIds.size > 0;

  // actors removed (or dimmed) by the filters — feeds the stats chip
  const filteredOutCount = filteredOutNodeIds.size;

  // a filtered-out node can't stay pinned in the Information panel
  useEffect(() => {
    if (!focusedNodeId || !graph || !graph.hasNode(focusedNodeId)) return;
    if (filtersHideNodes && filteredOutNodeIds.has(focusedNodeId)) {
      setFocusedNodeId(null);
    }
  }, [focusedNodeId, graph, filtersHideNodes, filteredOutNodeIds]);

  // actors the filters removed from the canvas — the Actors tab and the group
  // spotlight list only what is drawn (same rule as search: focusing a hidden
  // actor is a dead click). null when nothing is hidden, including dim mode,
  // where dimmed actors are still on the canvas
  const hiddenNodeIds = useMemo<Set<string> | null>(
    () => (filtersHideNodes ? filteredOutNodeIds : null),
    [filtersHideNodes, filteredOutNodeIds],
  );
  const onCanvas = useCallback(
    <M extends { id: string }>(members: M[]): M[] =>
      hiddenNodeIds ? members.filter((member) => !hiddenNodeIds.has(member.id)) : members,
    [hiddenNodeIds],
  );

  // platforms present in the graph, largest first — drives the Overview legend for
  // the platform color scheme
  const platforms = useMemo(() => {
    if (!graph) return [];
    const counts = new Map<string, number>();
    graph.forEachNode((_id, attrs) => {
      counts.set(attrs.platform, (counts.get(attrs.platform) ?? 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [graph]);
  // platform-scheme hues by rank — the same function the build stamped on the
  // nodes, so the legend dots match the canvas exactly
  const platformColors = useMemo(() => (graph ? rankPlatformColors(graph) : new Map<string, string>()), [graph]);

  // Filters-tab facet sections, built once so the panel rows and the canvas
  // chips share labels and order (roles minus the UI-hidden categories, same
  // set as the Actors tab rows)
  const nodeFilterSections = useMemo(
    () =>
      buildNodeFilterSections({
        t,
        platforms,
        roleGroups: roleGroups.filter(({ role }) => !HIDDEN_ACTOR_ROLE_CATEGORIES.includes(role)),
        zscoreGroups,
        options: nodeFilterOptions,
      }),
    [t, platforms, roleGroups, zscoreGroups, nodeFilterOptions],
  );

  // actors still matching every active filter — the number input's helper line
  const visibleActorCount = (graph?.order ?? 0) - filteredOutCount;

  const filterChips = useMemo(
    () =>
      buildFilterChips({
        t,
        sections: nodeFilterSections,
        nodeFilters,
        mentionTypes,
        selectedMentionTypes,
        edgeAnomalyOptions,
        hiddenEdgeAnomalyBuckets,
        mutualOnly,
      }),
    [t, nodeFilterSections, nodeFilters, mentionTypes, selectedMentionTypes, edgeAnomalyOptions, hiddenEdgeAnomalyBuckets, mutualOnly],
  );

  // a chip's × resets its whole filter type
  const removeFilterChip = useCallback((id: FilterChipId) => {
    switch (id.kind) {
      case 'facet':
        setNodeFilters((prev) => ({ ...prev, [id.category]: [] }));
        break;
      case 'minDegree':
        setNodeFilters((prev) => ({ ...prev, minDegree: 0 }));
        break;
      case 'minAvgZScore':
        setNodeFilters((prev) => ({ ...prev, minAvgZScore: null }));
        break;
      case 'mentionTypes':
        setSelectedMentionTypes([]);
        break;
      case 'anomalyBuckets':
        setHiddenEdgeAnomalyBuckets([]);
        break;
      case 'mutualOnly':
        setMutualOnly(false);
        break;
    }
  }, []);

  // community id → members (largest actors first) — feeds the Actors tab's
  // community drill-down list; roles/z-scores/flags carry their members inline
  // every actor's connections split by direction, for the per-actor readout
  // under member names in the group panel (PM 2026-10-16: see how active each
  // actor is without opening them one by one) — one neighbour walk per node,
  // O(edges) in total, so computed once per graph rather than per group
  const connectionsById = useMemo(() => {
    const map = new Map<string, MemberConnections>();
    graph?.forEachNode((id) => map.set(id, memberConnections(graph, id)));
    return map;
  }, [graph]);

  const communityMembers = useMemo(() => {
    const map = new Map<number, ActorGroupMember[]>();
    if (!graph) return map;
    const sizes = new Map<string, number>();
    graph.forEachNode((id, attrs) => {
      sizes.set(id, attrs.size);
      const entry = { id, label: attrs.label, platform: attrs.platform };
      const list = map.get(attrs.community);
      if (list) list.push(entry);
      else map.set(attrs.community, [entry]);
    });
    map.forEach((list) => list.sort((a, b) => (sizes.get(b.id) ?? 0) - (sizes.get(a.id) ?? 0)));
    return map;
  }, [graph]);

  // per-community structural metrics from the backend — Overview tab's Communities list
  const communityClusters = useMemo(() => {
    if (activeGraphKind === 'case' && caseGraphResponse) {
      return caseGraphResponse.clusters
        .map((c) => ({
          id: c.cluster_id,
          nodes: c.nodes,
          details: [
            `${t('High risk')}: ${Math.round(c.hra_share * 100)}%`,
            `${t('State affiliated')}: ${Math.round(c.state_affiliated_share * 100)}%`,
          ],
        }))
        .sort((a, b) => b.nodes - a.nodes);
    }
    if (activeGraphKind === 'mention' && currentModeData) {
      return currentModeData.clusters
        .map((c) => {
          // density is hidden for now (2026-09-25); homophily leads the row instead
          const details: string[] = [];
          if (c.homophily !== null) details.push(`${t('Homophily')}: ${c.homophily.toFixed(2)}`);
          if (c.conductance !== null) details.push(`${t('Conductance')}: ${c.conductance.toFixed(2)}`);
          if (c.edges_internal !== null && c.edges_external !== null) {
            details.push(`${t('Edges in/out')}: ${c.edges_internal}/${c.edges_external}`);
          }
          return { id: c.cluster_id, nodes: c.nodes, details };
        })
        .sort((a, b) => b.nodes - a.nodes);
    }
    return [];
  }, [activeGraphKind, caseGraphResponse, currentModeData, t]);

  // category spotlight → the canvas repaints members in one red/blue signal
  // hue (GraphHoverEffects); the legend mirrors it with a labeled swatch row
  const highlightedCategory = useMemo(() => {
    if (!graphHighlight || (graphHighlight.type !== 'role' && graphHighlight.type !== 'flag')) return null;
    const color = categoryHighlightColor(graphHighlight);
    if (!color) return null;
    const label =
      graphHighlight.type === 'role' ? t(graphHighlight.value) : t(NODE_FLAG_LABELS[graphHighlight.value]);
    return { label, color };
  }, [graphHighlight, t]);

  // resolve the active group spotlight into the right panel's display data;
  // null when the target vanished (graph rebuilt) — the panel simply hides
  const highlightGroup = useMemo<HighlightGroupInfo | null>(() => {
    if (!graphHighlight) return null;
    if (graphHighlight.type === 'community') {
      const id = graphHighlight.value;
      // density is hidden for now (2026-09-25) — no description line until it returns
      return {
        title: t('Community'),
        name: t('Community #{{id}}', { id }),
        members: onCanvas(communityMembers.get(id) ?? []),
        showZ: false,
      };
    }
    if (graphHighlight.type === 'role') {
      const group = roleGroups.find((g) => g.role === graphHighlight.value);
      if (!group) return null;
      return {
        title: t('Category'),
        name: t(group.role),
        description: ACTOR_ROLE_DESCRIPTIONS[group.role]
          ? t(ACTOR_ROLE_DESCRIPTIONS[group.role])
          : undefined,
        members: onCanvas(group.members),
        showZ: false,
      };
    }
    if (graphHighlight.type === 'zscore') {
      const group = zscoreGroups.find((g) => g.bucket === graphHighlight.value);
      if (!group) return null;
      return {
        title: t('Z-Score anomaly'),
        name: t(ZSCORE_BUCKET_LABELS[group.bucket]),
        description: t(ZSCORE_BUCKET_DESCRIPTIONS[group.bucket]),
        members: onCanvas(group.members),
        showZ: true,
      };
    }
    const group = flagGroups.find((g) => g.flag === graphHighlight.value);
    if (!group) return null;
    return {
      title: t('Category'),
      name: t(NODE_FLAG_LABELS[group.flag]),
      description: t(NODE_FLAG_DESCRIPTIONS[group.flag]),
      members: onCanvas(group.members),
      showZ: false,
    };
  }, [graphHighlight, communityMembers, roleGroups, zscoreGroups, flagGroups, onCanvas, t]);

  // rebuild graph when mode, sizeMetric, layout, or cached response changes: the
  // graph itself (metrics, colors, sizes) is built synchronously — that part is
  // fast — while the layout (seconds of FA2 on big graphs) runs in a Web Worker so
  // the page stays responsive; the previous graph remains visible until the new
  // positions arrive
  useEffect(() => {
    let built: { graph: Graph<NodeAttributes, EdgeAttributes>; stats: GraphStats };
    if (activeGraphKind === 'case') {
      if (!caseGraphResponse) return;
      built = buildCaseGraphFromResponse(caseGraphResponse, effectiveSizeMetric);
    } else {
      if (!currentModeData) return;
      built = buildGraphFromResponse(currentModeData, effectiveSizeMetric);
    }
    const { graph: g, stats: s } = built;

    const finish = () => {
      // bump the SigmaContainer key BEFORE handing over the new graph: react-sigma
      // otherwise kills + recreates the Sigma instance in place, and child effects
      // (hover/spotlight reducers, whose deps change on rebuild — e.g. the highlight
      // reset below) can run setSettings against the killed instance. That schedules
      // a render on an instance whose program map was emptied by kill() → "could not
      // find a suitable program for node type glowCircle". A key remount detaches
      // the old children before the kill, closing that window.
      graphVersionRef.current += 1;
      setGraph(g);
      setStats(s);
      // communities and roles can change across datasets — a stale spotlight would
      // silently dim the wrong nodes
      setGraphHighlight(null);
      setLayoutPending(false);
    };

    if (g.order === 0) {
      layoutWorkerRef.current?.terminate();
      layoutWorkerRef.current = null;
      finish();
      return;
    }

    setLayoutPending(true);
    // supersede any layout still computing — terminating the worker cancels it
    // immediately instead of letting it burn seconds on a stale layout
    layoutWorkerRef.current?.terminate();
    const worker = new Worker(new URL('./layoutWorker', import.meta.url));
    layoutWorkerRef.current = worker;

    worker.onmessage = (event: MessageEvent<LayoutWorkerResult>) => {
      if (layoutWorkerRef.current !== worker) return;
      let nonFinite = 0;
      event.data.nodes.forEach(({ id, x, y, isDust }) => {
        if (!g.hasNode(id)) return;
        // a single non-finite coordinate makes the Sigma constructor throw
        // ("Coordinates of node … are invalid") and leaves a dead black
        // canvas — park such nodes at the origin instead and say so loudly,
        // because the real bug is upstream in the layout math
        if (!Number.isFinite(x) || !Number.isFinite(y)) {
          nonFinite += 1;
          g.mergeNodeAttributes(id, { x: 0, y: 0, isDust });
          return;
        }
        g.mergeNodeAttributes(id, { x, y, isDust });
      });
      if (nonFinite > 0) {
        console.warn(
          `Social graph layout (${layoutMode}) produced ${nonFinite} non-finite node positions — parked at origin`,
        );
      }
      worker.terminate();
      layoutWorkerRef.current = null;
      finish();
    };
    worker.onerror = (event) => {
      if (layoutWorkerRef.current !== worker) return;
      worker.terminate();
      layoutWorkerRef.current = null;
      setLayoutPending(false);
      showRequestError(new Error(event.message || 'Layout failed'), t('Social graph'));
    };

    const request: LayoutWorkerRequest = { nodes: [], edges: [], layoutMode };
    g.forEachNode((id, attrs) => {
      request.nodes.push({ id, community: attrs.community, size: attrs.size });
    });
    g.forEachEdge((_edge, attrs, source, target) => {
      request.edges.push({ source, target, interCommunity: attrs.interCommunity });
    });
    worker.postMessage(request);
  }, [effectiveSizeMetric, layoutMode, currentModeData, activeGraphKind, caseGraphResponse, t]);

  // kill any in-flight layout when the page unmounts
  useEffect(() => () => layoutWorkerRef.current?.terminate(), []);

  // apply the selected color scheme and cross-cluster visibility; sigma repaints
  // on graphology attribute events
  useEffect(() => {
    if (!graph) return;
    graph.forEachNode((node, attrs) => {
      let nextColor = computeNodeColor(attrs, effectiveColorMode);
      // filtered-out actors (facet miss OR every connection removed by a
      // connection filter — see filteredOutNodeIds): by default they hide
      // outright (the user asked for a narrowed graph); with "Dim instead of
      // hide" they stay as spotlight-gray context instead — same flat
      // DIMMED_NODE_COLOR the panel spotlight uses, so "gray = outside the
      // selection" reads the same everywhere. Positions are untouched either
      // way, so clearing the filter restores the exact same picture.
      const failsFilters = filteredOutNodeIds.has(node);
      const nextNodeHidden = !dimFilteredNodes && failsFilters;
      const nextFilterDimmed = dimFilteredNodes && failsFilters;
      if (nextFilterDimmed) nextColor = DIMMED_NODE_COLOR;
      if (attrs.color !== nextColor) graph.setNodeAttribute(node, 'color', nextColor);
      if ((attrs.hidden ?? false) !== nextNodeHidden) graph.setNodeAttribute(node, 'hidden', nextNodeHidden);
      if ((attrs.filterDimmed ?? false) !== nextFilterDimmed) {
        graph.setNodeAttribute(node, 'filterDimmed', nextFilterDimmed);
      }
    });
    // the node loop above runs first, so sourceAttrs/targetAttrs hidden/filterDimmed are current
    graph.forEachEdge((edge, attrs, _source, _target, sourceAttrs, targetAttrs) => {
      // volume-sized (thick) edges get a higher alpha so they read solid, not smeared
      // top-volume pairs get the brightest treatment only in volume mode — in
      // classic mode they're ordinary edges
      const topVolume = volumeSizedEdges && attrs.topVolume === true;
      let nextColor = computeEdgeColor(
        attrs,
        effectiveColorMode,
        interClusterMode,
        zscoreEdgeColoring,
        volumeSizedEdges,
        topVolume,
      );
      // an edge touching a facet-dimmed node is context too — same RGB mute the
      // spotlight applies to edges outside the highlighted group (never alpha:
      // WebGL premultiply)
      if (sourceAttrs.filterDimmed || targetAttrs.filterDimmed) {
        nextColor = mixWithBackground(nextColor, CONTEXT_EDGE_MUTE);
      }
      if (attrs.color !== nextColor) graph.setEdgeAttribute(edge, 'color', nextColor);
      // resting draw order: top-volume pairs above everything so their thickness
      // isn't buried; in the connection scheme the colored flows above the gray
      // out ↔ out bundle — alpha alone can't lift them out of a dense fan
      // (WebGL premultiply), only drawing them last does
      const nextZIndex = topVolume
        ? 2
        : effectiveColorMode === 'connection' && isConnectionSurfacedFlow(attrs.scopeFlow)
          ? 1
          : 0;
      if ((attrs.zIndex ?? 0) !== nextZIndex) graph.setEdgeAttribute(edge, 'zIndex', nextZIndex);
      // explicit connection filters hide unconditionally — no anomaly/scope
      // exemptions — and the hover reducer keeps typeFiltered edges hidden
      const typeFiltered = isEdgeFiltered(attrs);
      if ((attrs.typeFiltered ?? false) !== typeFiltered) {
        graph.setEdgeAttribute(edge, 'typeFiltered', typeFiltered);
      }
      // anomalous edges stay visible even in "hidden" cross-cluster mode — hiding
      // them would hide exactly what z-score analysis is meant to reveal; same for
      // cross-boundary (in/out) edges in connection mode, and top-volume pairs
      // in volume mode (surfacing them is the point of the treatment). An edge
      // touching a facet-filtered node hides with the node, exemptions or not.
      const nextHidden =
        typeFiltered ||
        sourceAttrs.hidden === true ||
        targetAttrs.hidden === true ||
        (interClusterMode === 'hidden' &&
          attrs.interCommunity &&
          !topVolume &&
          !(zscoreEdgeColoring && zscoreEdgeStyle(attrs.zScore).anomalous));
      if ((attrs.hidden ?? false) !== nextHidden) graph.setEdgeAttribute(edge, 'hidden', nextHidden);
    });
  }, [
    graph,
    effectiveColorMode,
    interClusterMode,
    zscoreEdgeColoring,
    filteredOutNodeIds,
    isEdgeFiltered,
    volumeSizedEdges,
    dimFilteredNodes,
  ]);

  // flip the per-edge program between plain curves and arrowed curves; top-volume
  // pairs keep their arrows in volume mode even with the switch off (direction
  // on the heaviest flows is what the analyst is after)
  useEffect(() => {
    if (!graph) return;
    graph.forEachEdge((edge, attrs) => {
      const nextType =
        showEdgeArrows || (volumeSizedEdges && attrs.topVolume === true) ? 'curvedArrow' : 'curved';
      if (attrs.type !== nextType) graph.setEdgeAttribute(edge, 'type', nextType);
    });
  }, [graph, showEdgeArrows, volumeSizedEdges]);

  // PM rule for the "Thickness by interactions" default: ON for single-actor
  // explore graphs (few edges — the volume differences are the point), OFF for
  // everything else (dense graphs get visually heavy). Keyed on isExploreGraph
  // rather than the load handler so every load path (saved graph, WS reload,
  // POC loaders) gets the rule, while reloading the SAME kind of graph — e.g.
  // after a regenerate — leaves the user's manual choice alone.
  useEffect(() => {
    setVolumeSizedEdges(isExploreGraph);
  }, [isExploreGraph]);

  // "Thickness by interactions" switch: recompute every resting edge size from
  // the stored attributes (selection-time thickness always stays volume-scaled
  // via the reducer in GraphHoverEffects)
  useEffect(() => {
    if (!graph) return;
    graph.forEachEdge((edge, attrs) => {
      const nextSize = volumeSizedEdges
        ? edgeVolumeSize(attrs.totalCount)
        : classicEdgeSize(attrs.weight, attrs.reciprocal);
      if (attrs.size !== nextSize) graph.setEdgeAttribute(edge, 'size', nextSize);
    });
  }, [graph, volumeSizedEdges]);

  // load the saved graph for the route id; the stored result feeds the same
  // rebuild pipeline the POC loaders used (rawResponse / caseGraphResponse).
  // Guarded here, not just at the mount effect, so the WS-triggered reloads
  // below can't fire either if the flag flips at runtime.
  // The full-page /graphs/:graphId route renders OUTSIDE Layout, which is the only
  // place that loads the authoritative user record (/users/me). Without it currentUser
  // stays null, the social_graph flag never resolves, and loadSavedGraph (gated on the
  // flag to avoid the 403-logout) never fires — infinite loading on a hard reload.
  // When embedded in GraphViewOverlay, Layout has already populated it, so this no-ops.
  useEffect(() => {
    if (currentUser) return;
    API.fetch('GET', '/API/v1/users/me')
      .then((data: CurrentUser) => setCurrentUser(data))
      .catch((error) => {
        showRequestError(error, t('User'));
        // without the user record the feature flag never resolves and the graph
        // can never load — end the loading state so the failure message shows
        // instead of an infinite loader
        setLoading(false);
      });
  }, [currentUser, setCurrentUser, t]);

  const loadSavedGraph = useCallback((signal?: AbortSignal) => {
    if (!hasSocialGraphFlag && !graphSource) return;
    const id = Number(graphId);
    if (!Number.isInteger(id) || id <= 0) {
      setLoading(false);
      return;
    }
    setLoading(true);
    (graphSource ? graphSource(signal) : socialGraphApi.getGraph(id, signal))
      .then((detail) => {
        setSavedGraph(detail);
        setSearchQuery('');
        setFocusedNodeId(null);
        setTimelineWindow(null);
        setSelectedDayIndex(null);
        // `result` is the last successful run's payload — present even while a
        // regeneration is in flight, so the previous graph stays viewable
        if (detail.result) {
          if (detail.kind === 'actor') {
            const modes = detail.result as SocialGraphModesResponse;
            // explore graphs are exploratory-only and hide the Analysis mode
            // toggle — a 'zscore' mode carried over from a previously viewed
            // graph would render the empty zscore shell ("No relations found")
            // with no control left to switch back. Always normalized, NOT
            // inside the once-per-graph guard: mode has no reachable control
            // on an explore graph, so there's no user tweak to preserve.
            // (Unconditional set — reading `mode` here would stale-close over
            // the loadSavedGraph callback; React bails out on same-value sets.)
            if (modes.explore) setMode('exploratory');
            // single-actor explore graphs open with their own starting config
            // (Degree sizing + platform colors; layout may diverge later) —
            // applied once per graph so a reload after a regenerate keeps the
            // user's tweaks
            if (modes.explore && exploreDefaultsAppliedRef.current !== detail.id) {
              exploreDefaultsAppliedRef.current = detail.id;
              setSizeMetric(EXPLORE_GRAPH_DEFAULTS.sizeMetric);
              setColorMode(EXPLORE_GRAPH_DEFAULTS.colorMode);
              setLayoutMode(EXPLORE_GRAPH_DEFAULTS.layoutMode);
            }
            // Only update response state — the rebuild useEffect handles graph building
            setCaseGraphResponse(null);
            setActiveGraphKind('mention');
            setRawResponse(modes);
          } else {
            setRawResponse(null);
            setActiveGraphKind('case');
            setCaseGraphResponse(detail.result as CaseGraphResponse);
          }
        }
      })
      .catch((error) => showRequestError(error, t('Graph')))
      .finally(() => setLoading(false));
  }, [graphId, t, hasSocialGraphFlag, graphSource]);

  useEffect(() => {
    const controller = new AbortController();
    loadSavedGraph(controller.signal);
    return () => controller.abort();
  }, [loadSavedGraph]);

  // live status for this graph: patch the row on worker WS pushes and pull the
  // full detail (with the stored result) once a run reaches a terminal state
  useEffect(() => {
    const id = Number(graphId);
    if (!Number.isInteger(id) || id <= 0) return;
    const handleUpdate = (message: { data?: { id?: number; status?: string } }) => {
      const data = message?.data;
      if (data?.id !== id) return;
      setSavedGraph((prev) => (prev ? ({ ...prev, ...data } as SavedGraphDetailDTO) : prev));
      if (data.status === 'ready' || data.status === 'failed') loadSavedGraph();
    };
    websocketConnection.addHandler('SOCIAL_GRAPH', handleUpdate);
    return () => websocketConnection.removeHandler('SOCIAL_GRAPH', handleUpdate);
  }, [graphId, loadSavedGraph]);

  // matches are computed against a debounced copy of the query so typing stays
  // cheap on large graphs; the gap between the two drives the skeleton state
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearchQuery(searchQuery), 200);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const searchMatches = useMemo(() => {
    if (!graph) return [];
    const query = debouncedSearchQuery.trim().toLowerCase();
    if (query.length < 2) return [];
    const matches: { id: string; label: string; community: number }[] = [];
    graph.forEachNode((n, attrs) => {
      // filtered-out nodes are hidden on the canvas — focusing one would pan
      // the camera to an empty spot, so they don't surface as matches either
      // (dimmed ones in dim mode are still there, so they do)
      if (hiddenNodeIds?.has(n)) return;
      if (attrs.label.toLowerCase().includes(query)) {
        matches.push({ id: n, label: attrs.label, community: attrs.community });
      }
    });
    return matches.slice(0, 8);
  }, [graph, debouncedSearchQuery, hiddenNodeIds]);

  const searchLoading = searchQuery.trim().length >= 2 && searchQuery !== debouncedSearchQuery;

  // "Showing N of M actors from the actor group" note under the stats chips
  // (PM copy 2026-10-16, backend PR #935): splits the missing actors into those
  // the node cap squeezed out and those with no interactions, three variants
  // by which counts are non-zero. Graphs saved before the backend change carry
  // zeros for both, so they keep the old one-sentence explanation. Numbers are
  // locale-formatted (2,000 / 2 000) rather than left to interpolation.
  const hiddenActorsNote = useMemo(() => {
    // nodeCount includes external (out-of-scope) actors; only in-scope nodes
    // count against totalActorsInScope.
    const shownInScope = stats ? stats.nodeCount - stats.externalCount : 0;
    if (!stats || activeGraphKind !== 'mention' || isExploreGraph || stats.totalActorsInScope <= shownInScope) {
      return null;
    }
    const format = new Intl.NumberFormat(i18n.language);
    const counts = {
      shown: format.format(shownInScope),
      total: format.format(stats.totalActorsInScope),
      overLimit: format.format(stats.actorsOverLimit),
      noInteractions: format.format(stats.actorsWithoutInteractions),
      hidden: format.format(stats.totalActorsInScope - shownInScope),
    };
    if (stats.actorsOverLimit > 0 && stats.actorsWithoutInteractions > 0) {
      return t(
        'Showing {{shown}} of {{total}} actors from the actor group. {{overLimit}} actors couldn’t be shown because the graph reached its limit, and {{noInteractions}} actors have no interactions.',
        counts,
      );
    }
    if (stats.actorsOverLimit > 0) {
      return t(
        'Showing {{shown}} of {{total}} actors from the actor group. {{overLimit}} actors couldn’t be shown because the graph reached its limit.',
        counts,
      );
    }
    if (stats.actorsWithoutInteractions > 0) {
      return t(
        'Showing {{shown}} of {{total}} actors from the actor group. {{noInteractions}} actors have no interactions.',
        counts,
      );
    }
    return t(
      'Showing {{shown}} of {{total}} actors in scope - the remaining {{hidden}} had no mention, repost, or link relations and are not shown.',
      counts,
    );
  }, [stats, activeGraphKind, isExploreGraph, i18n.language, t]);

  // everything the legend reads — shared by the row renderer and the
  // "is there anything to show" check that hides the whole pill
  const legendProps: GraphLegendProps = {
    colorMode: effectiveColorMode,
    zscoreEdgeColoring,
    platforms,
    platformColors,
    communities: communityClusters.map((cluster) => cluster.id),
    effectiveSizeMetric,
    layoutMode,
    highlightedCommunity: graphHighlight?.type === 'community' ? graphHighlight.value : null,
    highlightedCategory,
    actorSelected: focusedNodeId !== null,
    volumeSizedEdges,
    filtersDimmed: dimFilteredNodes && filteredOutCount > 0,
    topVolumeEdges: volumeSizedEdges && (stats?.edgeCount ?? 0) >= TOP_VOLUME_MIN_EDGES,
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setFocusedNodeId(null);
  };

  const handleModeChange = (newMode: AnalysisMode) => {
    setMode(newMode);
    setFocusedNodeId(null);
    track('sg_analysis_mode_changed', { mode: newMode });
  };

  // Configuration-tab controls: one event with a `setting` discriminator, the
  // same shape as 'Use dashboard filter' (filter_type) on custom dashboards
  const trackSetting = useCallback(
    (setting: string, value: string | boolean) => track('sg_display_setting_changed', { setting, value }),
    [track],
  );
  const changeSizeMetric = useCallback(
    (value: NodeSizeMetric) => { setSizeMetric(value); trackSetting('size_metric', value); },
    [trackSetting],
  );
  const changeLayoutMode = useCallback(
    (value: LayoutMode) => { setLayoutMode(value); trackSetting('layout_mode', value); },
    [trackSetting],
  );
  const changeColorMode = useCallback(
    (value: GraphColorMode) => { setColorMode(value); trackSetting('color_scheme', value); },
    [trackSetting],
  );
  const changeInterClusterMode = useCallback(
    (value: InterClusterMode) => { setInterClusterMode(value); trackSetting('cross_cluster_links', value); },
    [trackSetting],
  );
  const changeShowEdgeArrows = useCallback(
    (value: boolean) => { setShowEdgeArrows(value); trackSetting('direction_arrows', value); },
    [trackSetting],
  );
  const changeVolumeSizedEdges = useCallback(
    (value: boolean) => { setVolumeSizedEdges(value); trackSetting('thickness_by_interactions', value); },
    [trackSetting],
  );
  const changeShowKeyConnectors = useCallback(
    (value: boolean) => { setShowKeyConnectors(value); trackSetting('critical_bridges', value); },
    [trackSetting],
  );

  const handleShowTimelineChange = (visible: boolean) => {
    setShowTimeline(visible);
    trackSetting('activity_timeline', visible);
    // turning the timeline off resets EVERYTHING to default (designer spec) —
    // the graph returns to exactly how it looked before touching the timeline
    if (!visible) {
      setTimelineWindow(null);
      setSelectedDayIndex(null);
      setHoveredPeriod(null);
      setIsPlaying(false);
      setPlayPos(null);
      setHiddenSeries([]);
      setPlaySpeed(1);
    }
  };

  const sigmaSettings = useMemo(
    () => ({
      defaultNodeType: 'glowCircle',
      nodeProgramClasses: { glowCircle: NodeGlowCircleProgram },
      defaultEdgeType: 'curved',
      edgeProgramClasses: { curved: EdgeCurveProgram, curvedArrow: EdgeCurvedArrowProgram },
      renderEdgeLabels: false,
      labelRenderedSizeThreshold: Infinity,
      labelColor: { color: LABEL_TEXT_COLOR },
      defaultDrawNodeLabel: createDrawNodeLabel(false),
      defaultDrawNodeHover: createDrawNodeLabel(false),
      zIndex: true,
      // gentler zoom steps — sigma's defaults (1.7 / 2.2) felt too abrupt
      zoomingRatio: 1.4,
      doubleClickZoomingRatio: 1.8,
      // longer glide per wheel notch (default 250) so steps blend instead of hopping
      zoomDuration: 450,
    }),
    [],
  );

  if (currentUser && !hasSocialGraphFlag && !isReadOnlyHost) {
    return <Navigate to="/" replace />;
  }

  const hostActions: GraphHostActions | null = isReadOnlyHost
    ? null
    : {
        addToGroup: openAddToGroup,
        addClusterToGroup,
        requestZScoreReport: handleZScoreReport,
        openGraphHome: () => navigate('/graphs'),
        exit: onExit ?? (() => navigate('/graphs')),
      };

  return (
    <SocialGraphAnalyticsContext.Provider value={track}>
    <GraphGuideContext.Provider value={guideApi}>
    <div className={isReadOnlyHost ? `${styles.container} ${styles.containerEmbedded}` : styles.container}>
      <div ref={graphWrapperRef} className={styles.graphWrapper}>
        <GraphHeader
          title={savedGraph?.name ?? t('Social graph')}
          actions={hostActions}
          onZScoreReport={
            zscoreReportAvailability === 'available' && !reportBusy
              ? hostActions?.requestZScoreReport
              : undefined
          }
          zscoreReportHidden={zscoreReportAvailability === 'none'}
          zscoreReportDisabledReason={
            zscoreReportAvailability === 'empty'
              ? t('The Z-score analysis found no actors in this graph, so there is nothing to report on.')
              : reportBusy
                ? t('The report is being generated — it will download automatically when ready.')
                : undefined
          }
          onExportPng={graph && graph.order > 0 ? handleExportPng : undefined}
          onExportSvg={graph && graph.order > 0 ? handleExportSvg : undefined}
          onGraphGuide={() => openGuide()}
        />
        {/* inside graphWrapper so the sheet is still visible while the wrapper
            is fullscreened via CanvasControls */}
        {guideOpen && (
          <GraphGuidePanel
            variant={guideVariantFor(activeGraphKind, isExploreGraph)}
            page={guidePage}
            section={guideSection}
            onPageChange={changeGuidePage}
            onClose={closeGuide}
          />
        )}
        <div
          className={styles.graphCanvasArea}
          // the timeline must never be covered by the left/right panels — while
          // it is visible, both panels stop above it via this clearance var
          style={{
            '--timeline-clearance':
              graph && graph.order > 0 && showTimeline && timelineData.length > 0
                ? '12.5rem'
                : '0px',
          } as React.CSSProperties}
        >
          {/* rail + panels stay reachable even when the current mode returns an
              empty graph — otherwise the user couldn't switch back to a mode with data */}
          {graph && (
            <GraphPanel
              activeGraphKind={activeGraphKind}
              isExploreGraph={isExploreGraph}
              mode={mode}
              handleModeChange={handleModeChange}
              sizeMetric={sizeMetric}
              setSizeMetric={changeSizeMetric}
              layoutMode={layoutMode}
              setLayoutMode={changeLayoutMode}
              colorMode={effectiveColorMode}
              setColorMode={changeColorMode}
              interClusterMode={interClusterMode}
              setInterClusterMode={changeInterClusterMode}
              showEdgeArrows={showEdgeArrows}
              setShowEdgeArrows={changeShowEdgeArrows}
              volumeSizedEdges={volumeSizedEdges}
              setVolumeSizedEdges={changeVolumeSizedEdges}
              showKeyConnectors={showKeyConnectors}
              setShowKeyConnectors={changeShowKeyConnectors}
              showTimeline={showTimeline}
              onShowTimelineChange={handleShowTimelineChange}
              zscoreEdgeColoring={zscoreEdgeColoring}
              communityClusters={communityClusters}
              communityMembers={communityMembers}
              roleGroups={roleGroups}
              zscoreGroups={zscoreGroups}
              flagGroups={flagGroups}
              hiddenNodeIds={hiddenNodeIds}
              mentionTypes={mentionTypes}
              selectedMentionTypes={selectedMentionTypes}
              onToggleMentionType={toggleMentionType}
              nodeFilters={nodeFilters}
              nodeFilterSections={nodeFilterSections}
              onToggleNodeFilter={toggleNodeFilter}
              dimFilteredNodes={dimFilteredNodes}
              onDimFilteredNodesChange={setDimFilteredNodes}
              onMinDegreeChange={setMinDegree}
              maxDegree={maxDegree}
              visibleActorCount={visibleActorCount}
              edgeAnomalyOptions={edgeAnomalyOptions}
              hiddenEdgeAnomalyBuckets={hiddenEdgeAnomalyBuckets}
              onToggleEdgeAnomalyBucket={toggleEdgeAnomalyBucket}
              mutualOnly={mutualOnly}
              onMutualOnlyChange={setMutualOnly}
              reciprocalCount={reciprocalCount}
              onMinAvgZScoreChange={setMinAvgZScore}
              actions={hostActions}
              graphHighlight={graphHighlight}
              setGraphHighlight={selectHighlight}
              setFocusedNodeId={selectNode}
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
              searchMatches={searchMatches}
              searchLoading={searchLoading}
              onClearSearch={handleClearSearch}
              onOpenChange={setLeftPanelOpen}
            />
          )}
          <div className={styles.graphCanvasInner}>
          {graph && graph.order > 0 ? (
            <>
              <div
                className={styles.sigmaStage}
                role="img"
                aria-label={
                  stats && stats.nodeCount > 0
                    ? t('Social graph with {{nodes}} actors and {{edges}} relations across {{communities}} communities', {
                        nodes: stats.nodeCount,
                        edges: stats.edgeCount,
                        communities: stats.communityCount,
                      })
                    : t('Social graph')
                }
              >
              <SigmaContainer
                key={graphVersionRef.current}
                graph={graph}
                settings={sigmaSettings}
                style={{ height: '100%', width: '100%' }}
              >
                <SigmaRefSync sigmaRef={sigmaRef} />
                <WebGLContextLossRecovery onContextLost={handleContextLost} restoreCamera={contextLossCameraRef} />
                <PixelRatioSync />
                <GraphHoverEffects
                  focusedNode={focusedNodeId}
                  showKeyConnectors={showKeyConnectors}
                  onFocusNode={selectNode}
                  activeEdgeKeys={activeEdgeKeys}
                  activeNodeIds={activeNodeIds}
                  brushNodeIds={brushNodeIds}
                  highlight={graphHighlight}
                  zscoreEdgeColoring={zscoreEdgeColoring}
                />
              </SigmaContainer>
              </div>
              {/* canvas action stack (Figma 353-169), top-right; slides left with the
                  Information panel */}
              <CanvasControls
                sigmaRef={sigmaRef}
                fullscreenTargetRef={graphWrapperRef}
                shifted={Boolean(selectedNodeInfo || highlightGroup)}
              />
              {/* general graph metrics as chips overlaid on the canvas (Figma 353-150);
                  pointer-events stay off so they never block graph interaction */}
              {stats && stats.nodeCount > 0 && (
                <div
                  className={`${styles.graphStatsOverlay} ${
                    leftPanelOpen ? styles.graphStatsOverlayShifted : ''
                  }`}
                >
                  <span className={styles.graphStatsChip}>
                    {t('Actors')} <strong>{stats.nodeCount}</strong>
                  </span>
                  {filteredOutCount > 0 && (
                    <span className={styles.graphStatsChip}>
                      {dimFilteredNodes ? t('Dimmed') : t('Filtered out')} <strong>{filteredOutCount}</strong>
                    </span>
                  )}
                  <span className={styles.graphStatsChip}>
                    {t('Relations')} <strong>{stats.edgeCount}</strong>
                  </span>
                  <span className={styles.graphStatsChip}>
                    {t('Communities')} <strong>{stats.communityCount}</strong>
                  </span>
                  {/* external-bridge / density / reciprocity / critical-bridge chips
                      were dropped on PM request (2026-09-16); the values stay in
                      `stats` for the Information panel and exports */}
                  {activeGraphKind === 'case' && (
                    <>
                      <span className={styles.graphStatsChip}>
                        {t('Producers')} <strong>{stats.producersCount ?? 0}</strong>
                      </span>
                      <span className={styles.graphStatsChip}>
                        {t('Amplifiers')} <strong>{stats.amplifiersCount ?? 0}</strong>
                      </span>
                    </>
                  )}
                  {/* honest only when the cap actually cut neighbours off (backend
                      `truncated`) — an un-truncated explore graph shows everyone */}
                  {isExploreGraph && rawResponse?.explore?.truncated && (
                    <span
                      className={styles.graphStatsNote}
                      title={t('Showing this actor’s {{count}} strongest connections.', {
                        count: rawResponse.explore.neighbours_returned,
                      })}
                    >
                      {t('Showing this actor’s {{count}} strongest connections.', {
                        count: rawResponse.explore.neighbours_returned,
                      })}
                    </span>
                  )}
                  {hiddenActorsNote && (
                    <span className={styles.graphStatsNote} title={hiddenActorsNote}>
                      {hiddenActorsNote}
                    </span>
                  )}
                  {/* active-filter chips wrap onto their own line under the metrics */}
                  <FilterChips chips={filterChips} onRemove={removeFilterChip} onClearAll={resetFilters} />
                </div>
              )}
              {/* bottom-left legend: collapsed pill ↔ open card (Figma 353-640 / 353-564 / 353-594);
                  gone entirely while it has nothing to say (e.g. Monochrome, nothing selected) */}
              {hasLegendContent(legendProps) && (
              <div
                className={`${styles.graphLegendCorner} ${
                  showTimeline && timelineData.length > 0 ? styles.graphLegendCornerRaised : ''
                } ${leftPanelOpen ? styles.graphLegendCornerShifted : ''}`}
              >
                <div className={`${styles.graphLegendShell} ${legendOpen ? styles.graphLegendShellOpen : ''}`}>
                  <button
                    type="button"
                    className={styles.graphLegendHead}
                    aria-expanded={legendOpen}
                    onClick={() => setLegendOpen((open) => !open)}
                  >
                    {t('Legend')}
                    <ChevronIcon
                      className={legendOpen ? undefined : styles.graphLegendChevronCollapsed}
                      aria-hidden="true"
                    />
                  </button>
                  {legendOpen && (
                    // the whole open card is the close target, not just the head
                    // button — the head stays the keyboard-accessible toggle, this
                    // click handler is a redundant pointer shortcut
                    <div className={styles.graphLegendBody} onClick={() => setLegendOpen(false)}>
                      <GraphLegend {...legendProps} />
                    </div>
                  )}
                </div>
              </div>
              )}
              {selectedNodeInfo ? (
                <NodePanel
                  // remount per actor: picking a connection (or clicking another
                  // node) opens the new actor's panel on the Info tab
                  key={selectedNodeInfo.nodeId}
                  info={selectedNodeInfo}
                  communityStats={selectedCommunityStats}
                  connections={selectedConnections}
                  // scope only exists on graphs built FROM an actor group —
                  // case/topic graphs and single-actor explore graphs have none
                  showActorGroupLabel={activeGraphKind === 'mention' && !isExploreGraph}
                  graphKind={activeGraphKind}
                  onSelectActor={selectNode}
                  onClose={() => selectNode(null)}
                  actions={hostActions}
                />
              ) : highlightGroup ? (
                <GroupPanel
                  group={highlightGroup}
                  connectionsById={connectionsById}
                  onSelectActor={selectNode}
                  onClose={() => setGraphHighlight(null)}
                  actions={hostActions}
                />
              ) : null}
              {showTimeline && timelineData.length > 0 && (
                <div className={styles.timelinePanel} ref={timelinePanelRef}>
                  <div className={styles.timelineHead}>
                    <div className={styles.timelineHeadGroup}>
                      <div className={styles.timelineTransport}>
                        <button
                          type="button"
                          className={styles.timelinePlayBtn}
                          onClick={handlePlayToggle}
                          disabled={playableIndices.length < 2}
                          aria-pressed={isPlaying}
                          aria-label={isPlaying ? t('Pause timeline playback') : t('Play timeline')}
                        >
                          {isPlaying ? (
                            <svg viewBox="0 0 24 24" width="10" height="10" aria-hidden="true">
                              <path fill="currentColor" d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" width="10" height="10" aria-hidden="true">
                              <path fill="currentColor" d="M8 5v14l11-7z" />
                            </svg>
                          )}
                        </button>
                        <button
                          type="button"
                          className={styles.timelineStepBtn}
                          onClick={() => stepSelectedDay(-1)}
                          disabled={isPlaying || selectedDayIndex === null || selectedDayIndex <= windowStartIdx}
                          aria-label={t('Previous day')}
                        >
                          <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
                            <path
                              d="M15 18l-6-6 6-6"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className={styles.timelineStepBtn}
                          onClick={() => stepSelectedDay(1)}
                          disabled={isPlaying || selectedDayIndex === null || selectedDayIndex >= windowEndIdx}
                          aria-label={t('Next day')}
                        >
                          <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
                            <path
                              d="M9 6l6 6-6 6"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                      <div className={styles.timelineSpeeds} role="group" aria-label={t('Playback speed')}>
                        {PLAY_SPEEDS.map((speed) => (
                          <button
                            key={speed}
                            type="button"
                            className={`${styles.timelineSpeedBtn} ${
                              playSpeed === speed ? styles.timelineSpeedBtnSelected : ''
                            }`}
                            onClick={() => setPlaySpeed(speed)}
                            aria-pressed={playSpeed === speed}
                          >
                            {speed}×
                          </button>
                        ))}
                      </div>
                      {timelineDayLabel && (
                        <span className={styles.timelineDayLabel} aria-live="polite">
                          {timelineDayLabel}
                        </span>
                      )}
                    </div>
                    <div className={styles.timelineHeadGroup}>
                      <span className={styles.timelineRangeLabel}>{timelineRangeLabel}</span>
                      <button
                        type="button"
                        className={styles.timelineResetBtn}
                        onClick={handleTimelineReset}
                        disabled={timelineAtDefaults}
                      >
                        {t('Reset')}
                      </button>
                      <span className={styles.timelineSep} aria-hidden="true" />
                      {/* legend doubles as the series filter; the last visible
                          series cannot be switched off (Figma Legend item note) */}
                      <div className={styles.timelineLegend}>
                        {timelineSeriesTypes.map((type) => {
                          const visible = !hiddenSeries.includes(type);
                          return (
                            <button
                              key={type}
                              type="button"
                              className={`${styles.timelineLegendItem} ${
                                visible ? '' : styles.timelineLegendItemOff
                              }`}
                              onClick={() => toggleTimelineSeries(type)}
                              disabled={visible && visibleSeriesTypes.length === 1}
                              aria-pressed={visible}
                            >
                              <span
                                className={styles.timelineLegendSwatch}
                                // off state grays the swatch to surface-3 (Figma 502-8)
                                style={{
                                  background: visible
                                    ? MENTION_TYPE_COLORS[type] ?? '#7C8597'
                                    : '#29292f',
                                }}
                                aria-hidden="true"
                              />
                              {t(MENTION_TYPE_LABELS[type] ?? type)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div className={styles.timelineChart}>
                    <SocialGraphTimeline
                      data={windowedTimelineData}
                      visibleTypes={visibleSeriesTypes}
                      selectedIndex={selectedDayIndex !== null ? selectedDayIndex - windowStartIdx : null}
                      onSelectDay={handleSelectDay}
                      onHoverPeriod={setHoveredPeriod}
                      activeIndex={playIndex !== null ? playIndex - windowStartIdx : null}
                    />
                    {timelineWindowEmpty && (
                      <p className={styles.timelineEmptyNote} role="status">
                        {t('No activity in this period')}
                      </p>
                    )}
                  </div>
                  <TimelineOverviewStrip
                    data={timelineData}
                    visibleTypes={visibleSeriesTypes}
                    window={[windowStartIdx, windowEndIdx]}
                    onWindowChange={handleWindowChange}
                    locked={isPlaying}
                  />
                </div>
              )}
              {(loading || layoutPending) && (
                <div className={styles.graphLoading} role="status" aria-live="polite">
                  <span className={styles.graphLoadingSpinner} aria-hidden="true" />
                  <span>{loading ? `${t('Updating')}…` : `${t('Computing layout')}…`}</span>
                </div>
              )}
            </>
          ) : (
            <div className={styles.empty}>
              {loading || layoutPending || (hasResponse && !graph) ? (
                `${t('Loading')}…`
              ) : hasResponse ? (
                t('No relations found')
              ) : savedGraph?.status === 'queued' || savedGraph?.status === 'generating' ? (
                t('The graph is being generated. This page will update automatically once it is ready.')
              ) : savedGraph?.status === 'failed' ? (
                <>
                  <span>{t('Generation failed')}</span>
                  {savedGraph.error_detail && <span className={styles.emptyDetail}>{savedGraph.error_detail}</span>}
                  <button
                    type="button"
                    className={styles.emptyAction}
                    onClick={() => navigate('/graphs')}
                  >
                    {t('Back to graphs')}
                  </button>
                </>
              ) : (
                t('Failed to load graph')
              )}
            </div>
          )}
          </div>
        </div>
      </div>

      {/* the route renders outside Layout, which owns the app-wide ToastContainer —
          without its own, showRequestError toasts would silently not render there.
          Embedded mode is inside Layout, whose container already shows them (a
          second mounted container would render every toast twice). */}
      {!isEmbedded && <ToastContainer />}

      {/* conditional mount matters: the modal resolves its source list from the
          `sources` prop in a mount-only effect */}
      {addToGroupIds && (
        <AddActorsToGroupModal
          isOpen
          sources={addToGroupIds.map((id) => ({ id }))}
          widgetName="Social Graph"
          dark
          onChange={() => setAddToGroupIds(null)}
        />
      )}
    </div>
    </GraphGuideContext.Provider>
    </SocialGraphAnalyticsContext.Provider>
  );
};
